import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import { EMAIL_REGEX } from '../../../helpers/constants'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import { selectUserData } from '../../../reducers/userDataReducer'
import Dashboard from '../../Dashboard'
import PageHeader from '../common/components/PageHeader'
import Steps from '../common/components/Steps'
import FormNavButtons from '../common/components/FormNavButtons'
import EventLoader from '../common/components/Loader'
import MobilePreview from '../common/components/mobilePreview/MobilePreview'
import { useCreateEvent } from '../common/hooks/useCreateEvent'
import { useEditEvent } from '../common/hooks/useEditEvent'
import { useConfirmationModal } from '../common/hooks/useConfirmationModal'
import { useGetEventById } from '../common/hooks/useGetEventById'

import FirstStep from './FirstStep'
import SecondStep from './SecondStep'
import ThirdStep from './ThirdStep'
import { useAppointmentFormState } from './hooks/useAppointmentFormState'
import { mapFormDataToAppointmentDto } from './mappers'

const AppointmentFormPage = () => {
  const { t } = useTranslation(['eventManagement', 'common'])
  const steps = t('eventManagement:appointmentFormPage.steps', { returnObjects: true })
  const [currentStep, setCurrentStep] = useState(0)

  const navigate = useNavigate()
  const { eventId } = useParams()

  const userData = useSelector(selectUserData)
  const selectedBuilding = useSelector(selectActiveBuilding)
  const selectedBuildingId = selectedBuilding ? selectedBuilding.id : null
  const selectedBuildingTimezone = selectedBuilding ? selectedBuilding.timezone : null

  const { event: eventData, isLoading: isEventLoading } = useGetEventById(eventId)

  const { askConfirmation, ConfirmationModal } = useConfirmationModal()

  const { isLoading: isCreateLoading, createEvent } = useCreateEvent({
    eventType: 'Appointment',
    redirectFn: () => navigate('/dashboard/calendar'),
  })
  const { isLoading: isEditLoading, editEvent } = useEditEvent({
    eventType: 'Appointment',
    redirectFn: () => navigate('/dashboard/calendar'),
  })

  const isLoading = isEventLoading || isCreateLoading || isEditLoading
  const isEditMode = !!eventData

  const {
    initialValues,
    validationSchema,
    eventTypeOptions,
    trainerOptions,
    availableOptions,
    areOptionsLoading,
  } = useAppointmentFormState({
    userData,
    eventData,
    t,
  })

  const submitForm = async (values) => {
    if (isLoading) {
      return
    }

    const formData = await mapFormDataToAppointmentDto(
      values,
      selectedBuildingId,
      selectedBuildingTimezone,
    )

    if (isEditMode) {
      const shouldOpenConfirmModal = !!eventData.repeatCorrelationId

      if (shouldOpenConfirmModal) {
        const confirmed = await askConfirmation()

        if (!confirmed) {
          return
        }

        editEvent(eventData.id, formData, confirmed)
        return
      }

      editEvent(eventData.id, formData, false)
      return
    }

    createEvent(formData)
  }

  const handleStepClick = (clickedStepIndex, stepsValidation) => {
    if (!stepsValidation[clickedStepIndex]) {
      return
    }

    setCurrentStep(clickedStepIndex)
  }

  return (
    <Dashboard.Container className="ctp-event-management">
      <EventLoader isLoading={isLoading || areOptionsLoading} />
      <PageHeader
        title={
          isEditMode
            ? t('eventManagement:appointmentFormPage.editTitle')
            : t('eventManagement:appointmentFormPage.createTitle')
        }
      />
      <div className="content-container">
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={submitForm}
          validateOnChange
          validateOnBlur
        >
          {({ setFieldValue, values, resetForm }) => {
            const {
              type,
              name,
              organizer,
              description,
              image,
              place,
              timeStart,
              timeEnd,
              frequency,
              positiveReview,
              replyTo,
              remindRsvp,
              remindRsvpValues,
              remindAll,
              remindAllValues,
            } = values

            const isFrequencyValid = isEditMode ? true : !!frequency
            const isRemindRsvpValid = remindRsvp
              ? !!remindRsvpValues.filter((p) => !!p).length
              : true
            const isRemindAllValid = remindAll ? !!remindAllValues.filter((p) => !!p).length : true
            const replyToValid = positiveReview ? !!replyTo && EMAIL_REGEX.test(replyTo) : true

            const stepsValidation = {
              0: type && name && organizer && description && image,
              1: place && timeStart && timeEnd && isFrequencyValid,
              2: replyToValid && isRemindRsvpValid && isRemindAllValid,
            }

            return (
              <>
                <div className="event-form-container">
                  <Steps
                    steps={steps}
                    currentStep={currentStep}
                    handleStepClick={(index) => handleStepClick(index, stepsValidation)}
                  />
                  <div className="event-form-container__form">
                    <FirstStep
                      displayStep={currentStep === 0}
                      eventTypeOptions={eventTypeOptions}
                      trainerOptions={trainerOptions}
                      formValues={values}
                      setFieldValue={setFieldValue}
                    />
                    <SecondStep
                      displayStep={currentStep === 1}
                      formValues={values}
                      setFieldValue={setFieldValue}
                      isEditMode={isEditMode}
                    />
                    <ThirdStep
                      displayStep={currentStep === 2}
                      formValues={values}
                      socialLinksOptions={availableOptions}
                      setFieldValue={setFieldValue}
                    />
                  </div>
                  <FormNavButtons
                    isFirstStep={!currentStep}
                    isLastStep={currentStep + 1 === steps.length}
                    isEditMode={isEditMode}
                    setStep={setCurrentStep}
                    publishButtonLabel={
                      isEditMode
                        ? t('common:actions.saveChanges')
                        : t('eventManagement:appointmentFormPage.publishButton')
                    }
                    onPublish={() => submitForm(values)}
                    onDiscard={() => resetForm(initialValues)}
                    onSaveDraft={() => {}}
                    nextButtonDisabled={!stepsValidation[currentStep] || isLoading}
                  />
                </div>
                <div className="event-form-mobile-preview-container">
                  <h2>{t('eventManagement:common.mobilePreview')}</h2>
                  <MobilePreview formState={values} eventType="Appointment" />
                </div>
              </>
            )
          }}
        </Formik>
      </div>
      <ConfirmationModal />
    </Dashboard.Container>
  )
}

export default AppointmentFormPage
